import {KeyboardEvent, useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import Skeleton from "react-loading-skeleton";
import Checkbox from "components/common/checkbox.component";
import {Tag, TagType} from "components/common/Tag.component";
import {Tooltip} from "components/tooltip/Tooltip";
import {SHARED_TOOLTIP_ID} from "components/tooltip/AppTooltip.component";
import {useTranslation} from "components/translations/useTranslation";
import {getWorkflowDetails} from "components/taskdetails-remake/taskDetails.action";
import * as reduxSelectors from "components/store/application.reducers";
import {WorkflowDetails} from "types/workflowDetailsWizard";
import {WorkflowDetailsPanelProps, WorkflowHistory} from "types/newWorkflowDetails";
import {StatusType} from "types/workflowDetailsWizardStep";
import {formatDate} from "utils/valueFormatter.function";
import WorkflowDetailsSteps from "./WorkflowDetailsSteps.component";
import "./newWorkflowDetails.css";

const HIDE_SKIPPED_STEPS_KEY = "hideSkippedSteps";

export function WorkflowDetailsPanel(props: Readonly<WorkflowDetailsPanelProps>) {
    const dispatch = useDispatch();
    const {translate} = useTranslation();

    const data: WorkflowDetails | null = useSelector(reduxSelectors.getTaskWorkflowDetails);
    const historyData: WorkflowHistory | null = useSelector(reduxSelectors.getTaskWorkflowHistory);

    // Starts loading: the effect fetches on mount, and the first paint should already show the skeleton
    const [loading, setLoading] = useState(true);
    const [hideSkippedSteps, setHideSkippedSteps] = useState(!!window.localStorage.getItem(HIDE_SKIPPED_STEPS_KEY));

    // Only a new task shows the skeleton; reloads after a document edit keep the current steps until the new ones arrive
    useEffect(() => {
        setLoading(true);
    }, [props.taskId]);

    useEffect(() => {
        // @ts-ignore the thunk resolves once the workflow and its history are in the store
        dispatch(getWorkflowDetails(props.taskId, props.isDocument)).then(() => setLoading(false));
    }, [props.taskId, props.taskDetails?.documentVersion]);

    const toggleSkippedSteps = () => {
        if (hideSkippedSteps) {
            window.localStorage.removeItem(HIDE_SKIPPED_STEPS_KEY);
        } else {
            window.localStorage.setItem(HIDE_SKIPPED_STEPS_KEY, "true");
        }
        setHideSkippedSteps(!hideSkippedSteps);
    }

    // A native checkbox toggles on Space only; Enter toggles it too, as keyboard users expect of this option
    const toggleSkippedStepsOnEnter = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== "Enter") return;
        event.preventDefault();
        toggleSkippedSteps();
    }

    // Nothing to show only once the load is over; while it runs the shell and skeleton are shown
    if (!loading && !data) return null;

    const steps = data?.process?.steps ?? [];
    const hasSkippedSteps = steps.some((step) => step.status === StatusType.SKIPPED);
    const fourEyesActive = !!data?.process?.twoPersonsRuleStatus?.planned;

    return (
        <div className={`new-workflow-details ${props.className || ''}`}
             data-cy="taskDetail.workflowDetailsPanel">
            <div className="new-workflow-details__header">
                <strong className="new-workflow-details__title">{translate("taskDetail.workflowDetails")}</strong>
                <div className="new-workflow-details__description-row">
                    <dl className="new-workflow-details__dates">
                        {props.documentDueDate &&
                            <div className="new-workflow-details__date" data-cy="workflowDetails.documentDueDate">
                                <dt>{translate("taskDetail.documentDueDate")}</dt>
                                <dd>{formatDate(props.documentDueDate, null)}</dd>
                            </div>}
                        {props.taskDueDate &&
                            <div className="new-workflow-details__date" data-cy="workflowDetails.taskDueDate">
                                <dt>{translate("taskDetail.taskDueDate")}</dt>
                                <dd>{formatDate(props.taskDueDate, null)}</dd>
                            </div>}
                    </dl>
                    {(fourEyesActive || hasSkippedSteps) &&
                        <div className="new-workflow-details__options">
                            {fourEyesActive &&
                                // A button, so keyboard users can focus it to open the tooltip that explains the tag as
                                // well. The shared tooltip renders nothing of its own to point to, so screen readers get
                                // the explanation from a hidden copy of it
                                <button type="button" className="new-workflow-details__four-eyes" data-cy="workflowDetails.fourEyes"
                                        data-tip="four-eyes-tooltip" data-for={SHARED_TOOLTIP_ID}
                                        aria-describedby="four-eyes-description">
                                    <Tag type={TagType.INFORMATION} message={translate("taskDetail.4-eyesActive")}/>
                                    <span id="four-eyes-description" className="sr-only">
                                        {translate("workflowDetails.4EyesApproval.checkboxInfo")}
                                    </span>
                                    <Tooltip id="four-eyes-tooltip"
                                             content={() => translate("workflowDetails.4EyesApproval.checkboxInfo")}/>
                                </button>}
                            {hasSkippedSteps &&
                                <div className="new-workflow-details__hide-skipped">
                                    <Checkbox isChecked={hideSkippedSteps}
                                              onChange={toggleSkippedSteps}
                                              onKeyDown={toggleSkippedStepsOnEnter}
                                              label={translate("taskDetail.hideSkippedSteps")}
                                              dataCy="workflowDetails.hideSkippedSteps"/>
                                </div>}
                        </div>}
                </div>
            </div>
            <div className="new-workflow-details__body">
                {loading || !data ?
                    <div className="new-workflow-details__loading" data-cy="workflowDetails.loading">
                        <Skeleton count={4} height={56}/>
                    </div> :
                    <WorkflowDetailsSteps data={data}
                                          events={historyData?.events ?? []}
                                          hideSkippedSteps={hideSkippedSteps}/>}
            </div>
        </div>
    );
}

export default WorkflowDetailsPanel;
